<template>
  <v-app class="pos-app">
    <!-- ===================================================== -->
    <!-- HEADER -->
    <!-- ===================================================== -->

    <PosHeader
      :pinned="isPinned"
      :terminal="state.terminal"
      :session="state.session"
      :session-busy="state.sessionBusy"

      @toggle-sidebar="toggleSidebar"
      @toggle-pin="togglePin"

      @open-terminal="openTerminal"
      @close-terminal="closeTerminal"

      @start-session="openStartSession"
      @end-session="openCloseSession"
    />

    <!-- ===================================================== -->
    <!-- MAIN AREA -->
    <!-- ===================================================== -->

    <div class="pos-shell">
      <!-- =================================================== -->
      <!-- DESKTOP CATEGORY SIDEBAR -->
      <!-- =================================================== -->

      <aside
        v-if="mdAndUp && sidebarOpen"
        class="pos-sidebar-desktop"
      >
        <PosSidebar />
      </aside>

      <!-- =================================================== -->
      <!-- MOBILE CATEGORY SIDEBAR -->
      <!-- =================================================== -->

      <v-navigation-drawer
        v-if="!mdAndUp"
        v-model="sidebarOpen"
        temporary
        width="260"
        class="pos-navigation-drawer"
      >
        <PosSidebar />
      </v-navigation-drawer>

      <!-- =================================================== -->
      <!-- ROUTER CONTENT -->
      <!-- =================================================== -->

      <main class="pos-main">
        <router-view />
      </main>
    </div>

    <!-- ===================================================== -->
    <!-- FOOTER -->
    <!-- ===================================================== -->

    <PosFooter />

    <!-- ===================================================== -->
    <!-- TERMINAL SELECTOR -->
    <!-- ===================================================== -->

    <PosTerminalSelectorDialog
      v-model="terminalSelectorDialog"
      :terminals="state.terminals"
      :current-terminal-id="
        state.terminal?.terminalId
        ??
        state.terminal?.id
        ??
        null
      "
      :loading="state.loadingTerminals"
      :error="state.terminalError"
      :allow-close="true"
      @select="selectTerminal"
    />

    <!-- ===================================================== -->
    <!-- START SESSION -->
    <!-- ===================================================== -->

    <PosStartSessionDialog
      v-model="startSessionDialog"
      :terminal="state.terminal"
      :loading="state.sessionBusy"
      :error="state.sessionError"
      :currencies="sessionCurrencies"
      :default-currency="defaultSessionCurrency"
      @start="startSession"
    />

    <!-- ===================================================== -->
    <!-- CLOSE SESSION -->
    <!-- ===================================================== -->

    <PosCloseSessionDialog
      v-model="closeSessionDialog"
      :session="state.session"
      :loading="state.sessionBusy"
      :error="state.sessionError"
      @close-session="closeSession"
    />
  </v-app>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch
} from "vue";

import {
  useDisplay
} from "vuetify";

import PosHeader
  from "@/modules/pos/components/PosHeader.vue";

import PosSidebar
  from "@/modules/pos/components/PosSidebar.vue";

import PosFooter
  from "@/modules/pos/components/PosFooter.vue";

import PosStartSessionDialog
  from "@/modules/pos/components/session/PosStartSessionDialog.vue";

import PosCloseSessionDialog
  from "@/modules/pos/components/session/PosCloseSessionDialog.vue";

import PosTerminalSelectorDialog
  from "@/modules/pos/components/terminal/PosTerminalSelectorDialog.vue";

import posSessionService
  from "@/modules/pos/services/posSessionService";

import posTerminalService
  from "@/store/admin/posTerminals";

import posStore
  from "@/store/pos/posStore";

import posState
  from "@/store/pos/posState";

import posPersistence
  from "@/store/pos/posPersistence";

import offlineManager
  from "@/platform/pwa/offlineManager";

import indexedDb
  from "@/platform/storage/indexedDb";

import syncQueue
  from "@/platform/storage/syncQueue";

import posSessionOfflinePersistence
  from "@/store/pos/posSessionOfflinePersistence";

import posSyncProcessor
  from "@/store/pos/posSyncProcessor";

// ============================================================
// DISPLAY
// ============================================================

const {
  mdAndUp
} =
  useDisplay();

// ============================================================
// POS STATE
// ============================================================

const state =
  posState.state;

// ============================================================
// SESSION CURRENCY CONFIGURATION
// Currency options currently match the existing selector catalog.
// Replace with tenant-authorized currencies when that API is wired.
// This list is NOT a default currency and does not override a session.
// ============================================================

const sessionCurrencies = [
  { title: "Central African CFA franc (XAF)", value: "XAF" },
  { title: "Nigerian Naira (NGN)", value: "NGN" },
  { title: "South African Rand (ZAR)", value: "ZAR" },
  { title: "West African CFA franc (XOF)", value: "XOF" },
  { title: "Australian Dollar (AUD)", value: "AUD" },
  { title: "British Pound (GBP)", value: "GBP" },
  { title: "Canadian Dollar (CAD)", value: "CAD" },
  { title: "Euro (EUR)", value: "EUR" },
  { title: "US Dollar (USD)", value: "USD" }
];

// A terminal may suggest a currency, but never silently choose CAD.
// The cashier must explicitly select the currency before opening.
const defaultSessionCurrency = computed(() => {
  const code = normalizeCurrencyCode(
    state.terminal?.currencyCode ??
    state.terminal?.currency
  );

  return sessionCurrencies.some(item => item.value === code)
    ? code
    : null;
});

// ============================================================
// TERMINAL MODE / SESSION POLICY
//
// Backend PosTerminalDto exposes:
//   isSelfService
//   isMobile
//   persistSession
//   clearSessionAfterTransaction
//
// The frontend also derives the policy from isSelfService /
// isMobile as a fallback for older cached terminal records.
//
// Assisted:
//   persist session.
//
// Self-service:
//   clear session after a completed operation.
//
// Mobile:
//   clear session after a completed operation.
// ============================================================

const isSelfServiceMode =
  computed(() =>
    state.terminal?.isSelfService ===
      true
    ||
    state.terminal?.IsSelfService ===
      true
  );

const isMobileMode =
  computed(() =>
    !isSelfServiceMode.value
    &&
    (
      state.terminal?.isMobile ===
        true
      ||
      state.terminal?.IsMobile ===
        true
    )
  );

const isAssistedMode =
  computed(() =>
    !isSelfServiceMode.value
    &&
    !isMobileMode.value
  );

const shouldPersistSession =
  computed(() => {
    const configured =
      state.terminal?.persistSession
      ??
      state.terminal?.PersistSession;

    if (
      typeof configured ===
      "boolean"
    ) {
      return configured;
    }

    return isAssistedMode.value;
  });

const shouldClearSessionAfterTransaction =
  computed(() => {
    const configured =
      state.terminal?.clearSessionAfterTransaction
      ??
      state.terminal?.ClearSessionAfterTransaction;

    if (
      typeof configured ===
      "boolean"
    ) {
      return configured;
    }

    return (
      isSelfServiceMode.value
      ||
      isMobileMode.value
    );
  });

// ============================================================
// SIDEBAR
// ============================================================

const sidebarOpen =
  ref(true);

const isPinned =
  ref(true);

// ============================================================
// DIALOGS
// ============================================================

const terminalSelectorDialog =
  ref(false);

const startSessionDialog =
  ref(false);

const closeSessionDialog =
  ref(false);

// ============================================================
// PERSISTENCE INITIALIZATION
//
// Prevent startup terminal/session resets from overwriting
// previously persisted POS transaction state.
// ============================================================

const persistenceReady =
  ref(false);

// ============================================================
// OFFLINE POS REFERENCE CACHE
// ============================================================

const POS_CACHE_KEYS = {
  terminals:
    "pos.terminals",

  locations:
    "pos.locations"
};

// ============================================================
// SIDEBAR
// ============================================================

function toggleSidebar() {
  sidebarOpen.value =
    !sidebarOpen.value;
}

function togglePin() {
  isPinned.value =
    !isPinned.value;

  if (
    isPinned.value
  ) {
    sidebarOpen.value =
      true;
  }
}

// ============================================================
// TERMINAL ID
// ============================================================

function getTerminalId(
  terminal
) {
  return (
    terminal?.id
    ??
    terminal?.terminalId
    ??
    null
  );
}

// ============================================================
// SESSION ID
// ============================================================

function getSessionId(
  session
) {
  return (
    session?.id
    ??
    session?.sessionId
    ??
    null
  );
}

// ============================================================
// LOCK SESSION CURRENCY
//
// Session currency is authoritative. Never use a global default.
// A legacy session without a valid currency remains visible for
// reconciliation/closing, but cannot supply a transaction currency.
// ============================================================

function normalizeCurrencyCode(value) {
  const code = String(value ?? "").trim().toUpperCase();
  return /^[A-Z]{3}$/.test(code) ? code : null;
}

function applySession(session) {
  if (!session) {
    state.session = null;
    state.currency = null;
    return;
  }

  const currencyCode = normalizeCurrencyCode(
    session.currencyCode ?? session.CurrencyCode
  );

  state.session = {
    ...session,
    currencyCode: currencyCode ?? ""
  };

  // Do not reuse currency from the preceding session or cart.
  state.currency = currencyCode;

  if (!currencyCode) {
    state.sessionError =
      "This POS session has no valid currency. " +
      "Reconcile the session before processing transactions.";
  }
}

// ============================================================
// HAS ACTIVE SESSION
// ============================================================

// ============================================================
// SESSION LIFECYCLE STATUS
//
// A session ID alone does not mean that the session is open.
// Treat explicitly closed sessions as inactive. Unknown legacy
// statuses remain active until the backend confirms closure,
// preventing accidental concurrent sessions.
// ============================================================

function isSessionOpen(session) {
  if (!session?.id && !session?.sessionId) {
    return false;
  }

  if (
    session.closedAtUtc ||
    session.closedAt ||
    session.closedOnUtc ||
    session.endedAtUtc
  ) {
    return false;
  }

  const status = String(
    session.status ?? session.sessionStatus ?? ""
  ).trim().toUpperCase();

  return ![
    "CLOSED",
    "CLOSE",
    "CANCELLED",
    "CANCELED",
    "ENDED"
  ].includes(status);
}

function hasActiveSession() {
  return isSessionOpen(state.session);
}

// ============================================================
// PERSIST ASSISTED SESSION IN INDEXEDDB
//
// Online backend sessions do not naturally require a local
// clientSessionId. posSessionOfflinePersistence.saveSession()
// uses clientSessionId as the IndexedDB key, so for a normal
// online assisted session the authoritative server session ID
// is also used as the local clientSessionId.
//
// If an offline-created session was synchronized earlier, the
// existing client/server mapping is reused.
//
// Self-service and Mobile sessions are not persisted here for
// reuse between customer operations.
// ============================================================

async function persistSessionForTerminalMode(
  session
) {
  if (
    !session
    ||
    !shouldPersistSession.value
  ) {
    return null;
  }

  const sessionId =
    getSessionId(
      session
    );

  const terminalId =
    session?.terminalId
    ??
    getTerminalId(
      state.terminal
    );

  if (
    !sessionId
    ||
    !terminalId
  ) {
    return null;
  }

  const serverSessionId =
    String(
      session?.serverSessionId
      ??
      sessionId
    );

  let existingLocal =
    null;

  try {
    existingLocal =
      await posSessionOfflinePersistence
        .getByServerSessionId(
          serverSessionId
        );
  }
  catch (error) {
    console.warn(
      "[POS] Existing local session mapping could not be resolved:",
      error
    );
  }

  const clientSessionId =
    String(
      existingLocal?.clientSessionId
      ??
      session?.clientSessionId
      ??
      sessionId
    );

  const plainSession =
    JSON.parse(
      JSON.stringify(
        session
      )
    );

  const persistedSession = {
    ...existingLocal,
    ...plainSession,

    clientSessionId,

    currencyCode: normalizeCurrencyCode(session?.currencyCode) ?? "",

    id:
      clientSessionId,

    sessionId:
      clientSessionId,

    serverSessionId,

    terminalId:
      String(
        terminalId
      ),

    terminalCode:
      session?.terminalCode
      ??
      state.terminal?.terminalCode
      ??
      "",

    terminalName:
      session?.terminalName
      ??
      state.terminal?.terminalName
      ??
      "",

    status:
      "OPEN",

    syncStatus:
      "SYNCHRONIZED",

    wasCreatedOffline:
      existingLocal?.wasCreatedOffline ===
        true
      ||
      session?.wasCreatedOffline ===
        true
  };

  const saved =
    await posSessionOfflinePersistence
      .saveSession(
        persistedSession
      );

  console.log(
    "[POS] Assisted session persisted in IndexedDB:",
    {
      terminalId:
        persistedSession.terminalId,

      clientSessionId:
        persistedSession.clientSessionId,

      serverSessionId:
        persistedSession.serverSessionId
    }
  );

  return saved;
}

// ============================================================
// DELETE LOCAL SESSION AFTER EXPLICIT ONLINE CLOSE
//
// Offline close records must remain until their queued close job
// synchronizes, so this helper is used only after a successful
// online backend close.
// ============================================================

async function deleteLocalSessionAfterOnlineClose(
  session
) {
  const terminalId =
    session?.terminalId
    ??
    getTerminalId(
      state.terminal
    );

  if (
    !terminalId
  ) {
    return;
  }

  try {
    const localSession =
      shouldPersistSession.value
        ? await posSessionOfflinePersistence
            .getOpenByTerminalId(
              terminalId
            )
        : null;

    const clientSessionId =
      localSession?.clientSessionId
      ??
      session?.clientSessionId
      ??
      null;

    if (
      clientSessionId
    ) {
      await posSessionOfflinePersistence
        .deleteSession(
          clientSessionId
        );

      console.log(
        "[POS] Closed session removed from IndexedDB:",
        clientSessionId
      );
    }
  }
  catch (error) {
    console.warn(
      "[POS] Closed session IndexedDB cleanup failed:",
      error
    );
  }
}

// ============================================================
// APPLY TERMINAL
//
// Selects a terminal for this POS runtime.
//
// It does NOT activate/deactivate the backend terminal.
// ============================================================

function applyTerminal(
  terminal
) {
  if (!terminal) {
    state.terminal =
      null;

    return;
  }

  const terminalId =
    getTerminalId(
      terminal
    );

  state.currency = null;

  state.terminal = {
    ...terminal,

    id:
      terminalId,

    terminalId,

    terminalCode:
      terminal.terminalCode
      ??
      terminal.code
      ??
      "",

    terminalName:
      terminal.terminalName
      ??
      terminal.name
      ??
      "",

    terminalLocationId:
      terminal.terminalLocationId
      ??
      terminal.locationId
      ??
      null,

    terminalLocationName:
      terminal.terminalLocationName
      ??
      terminal.locationName
      ??
      "",

    terminalLocationCode:
      terminal.terminalLocationCode
      ??
      terminal.locationCode
      ??
      ""
  };

  console.log(
    "[POS] Runtime terminal selected:",
    state.terminal
  );
}

// ============================================================
// CONNECTIVITY
// ============================================================

function isOnline() {
  try {
    return offlineManager
      .isOnlineNow();
  }
  catch {
    return (
      typeof navigator ===
        "undefined"
        ? true
        : navigator.onLine !== false
    );
  }
}

// ============================================================
// CURRENT USER ID
// ============================================================

function getCurrentUserId() {
  return (
    localStorage.getItem(
      "userId"
    )
    ??
    sessionStorage.getItem(
      "userId"
    )
    ??
    null
  );
}

// ============================================================
// CACHE PUT
//
// IMPORTANT:
//
// IndexedDB must receive plain structured-cloneable objects.
// Vue reactive Proxy objects cannot reliably be persisted
// directly.
// ============================================================

async function saveCache(
  key,
  value
) {
  try {
    const plainValue =
      value === null ||
      value === undefined
        ? value
        : JSON.parse(
            JSON.stringify(
              value
            )
          );

    await indexedDb.put(
      "cache",
      {
        key,

        value:
          plainValue,

        updatedAtUtc:
          new Date()
            .toISOString()
      }
    );

    console.log(
      `[POS] Cached ${key}:`,
      plainValue
    );
  }
  catch (error) {
    console.error(
      `[POS] Unable to cache ${key}:`,
      error
    );

    throw error;
  }
}

// ============================================================
// CACHE GET
// ============================================================

async function loadCache(
  key
) {
  try {
    const record =
      await indexedDb.get(
        "cache",
        key
      );

    return record?.value
      ??
      null;
  }
  catch (error) {
    console.warn(
      `[POS] Unable to restore ${key}:`,
      error
    );

    return null;
  }
}

// ============================================================
// BUILD LOCATION CACHE
// ============================================================

function buildLocationsFromTerminals(
  terminals
) {
  const locations =
    new Map();

  for (
    const terminal
    of (
      Array.isArray(
        terminals
      )
        ? terminals
        : []
    )
  ) {
    const locationId =
      terminal?.terminalLocationId
      ??
      terminal?.locationId
      ??
      null;

    if (!locationId) {
      continue;
    }

    const key =
      String(
        locationId
      );

    if (
      locations.has(
        key
      )
    ) {
      continue;
    }

    locations.set(
      key,
      {
        id:
          locationId,

        locationId,

        code:
          terminal?.terminalLocationCode
          ??
          terminal?.locationCode
          ??
          "",

        locationCode:
          terminal?.terminalLocationCode
          ??
          terminal?.locationCode
          ??
          "",

        name:
          terminal?.terminalLocationName
          ??
          terminal?.locationName
          ??
          "",

        locationName:
          terminal?.terminalLocationName
          ??
          terminal?.locationName
          ??
          ""
      }
    );
  }

  return Array.from(
    locations.values()
  );
}

// ============================================================
// TERMINAL ACTIVE STATE
//
// Handle the common DTO forms that may arrive from backend or
// older cached terminal records.
// ============================================================

function isTerminalActive(
  terminal
) {
  if (!terminal) {
    return false;
  }

  if (
    terminal.isActive === true ||
    terminal.IsActive === true ||
    terminal.active === true
  ) {
    return true;
  }

  const status =
    String(
      terminal.status
      ??
      terminal.terminalStatus
      ??
      ""
    )
      .trim()
      .toUpperCase();

  return (
    status === "ACTIVE" ||
    status === "OPEN" ||
    status === "ENABLED"
  );
}

// ============================================================
// GET ACTIVE TERMINALS
// ============================================================

function getActiveTerminals() {
  return (
    Array.isArray(
      state.terminals
    )
      ? state.terminals.filter(
          terminal =>
            isTerminalActive(
              terminal
            )
        )
      : []
  );
}

// ============================================================
// LOAD AVAILABLE TERMINALS
//
// OFFLINE:
//   IndexedDB first.
//
// ONLINE:
//   backend first.
//   cache PLAIN terminal objects.
//   then assign them to Vue reactive state.
//
// Backend failure:
//   IndexedDB fallback.
// ============================================================

async function loadTerminals() {
  state.loadingTerminals =
    true;

  state.terminalError =
    null;

  try {
    // ========================================================
    // KNOWN OFFLINE
    // ========================================================

    // Browser disconnection is the only reason to skip the API.
    // A previously latched application Offline state must not block retry.
    if (
      typeof navigator !== "undefined" &&
      navigator.onLine === false
    ) {
      const cachedTerminals =
        await loadCache(
          POS_CACHE_KEYS.terminals
        );

      state.terminals =
        Array.isArray(
          cachedTerminals
        )
          ? cachedTerminals
          : [];

      console.log(
        "[POS] Offline terminals restored from IndexedDB:",
        state.terminals
      );

      if (
        getActiveTerminals()
          .length ===
        0
      ) {
        state.terminalError =
          "No cached active POS terminals are available.";
      }

      return;
    }

    // ========================================================
    // TRY BACKEND
    // ========================================================

    const terminals =
      await posTerminalService
        .getTerminals();

    /*
     * A successful HTTP/API call proves that the application
     * currently has backend connectivity.
     */
    offlineManager
      .reportNetworkSuccess(
        "pos-terminals-load-success"
      );

    // ========================================================
    // CREATE PLAIN CACHEABLE TERMINALS
    // ========================================================

    const plainTerminals =
      Array.isArray(
        terminals
      )
        ? terminals.map(
            terminal => ({
              ...terminal
            })
          )
        : [];

    // ========================================================
    // CACHE TERMINALS
    // ========================================================

    try {
      await saveCache(POS_CACHE_KEYS.terminals, plainTerminals);
    } catch (cacheError) {
      console.warn("[POS] Could not cache terminals:", cacheError);
    }

    // ========================================================
    // CACHE LOCATIONS
    // ========================================================

    try {
      await saveCache(
        POS_CACHE_KEYS.locations,
        buildLocationsFromTerminals(plainTerminals)
      );
    } catch (cacheError) {
      console.warn("[POS] Could not cache locations:", cacheError);
    }

    // ========================================================
    // APPLY TO REACTIVE STATE
    // ========================================================

    state.terminals =
      plainTerminals;

    if (
      getActiveTerminals()
        .length ===
      0
    ) {
      state.terminalError =
        "No active POS terminals are available. " +
        "An administrator must create or activate a terminal.";
    }

    console.log(
      "[POS] Available terminals loaded from backend:",
      state.terminals
    );
  }
  catch (error) {
    // ========================================================
    // DETERMINE WHETHER THIS WAS AN HTTP ERROR OR A REAL
    // CONNECTIVITY FAILURE
    // ========================================================

    const hasHttpResponse = Boolean(error?.response);
    const isTransportFailure =
      !hasHttpResponse &&
      (error?.code === "ERR_NETWORK" ||
       error?.code === "ECONNABORTED" ||
       error?.code === "ETIMEDOUT" ||
       (typeof navigator !== "undefined" && navigator.onLine === false));

    if (
      hasHttpResponse
    ) {
      /*
       * 400 / 401 / 403 / 404 / 409 / 500 etc. still prove
       * that the backend was reachable.
       */
      offlineManager
        .reportNetworkSuccess(
          "pos-terminals-http-response"
        );

      console.warn(
        "[POS] Backend terminal request returned an HTTP error. " +
        "Trying IndexedDB fallback:",
        error
      );
    }
    else if (isTransportFailure) {
      offlineManager.markOffline("pos-terminals-backend-unreachable");

      console.warn(
        "[POS] Terminal backend unreachable. " +
        "Application switched to offline mode. " +
        "Loading IndexedDB terminal cache.",
        error
      );
    }

    else {
      console.error("[POS] Terminal loading code error:", error);
      state.terminalError = error?.message || "Unable to load POS terminals.";
      return;
    }

    // ========================================================
    // INDEXEDDB FALLBACK
    // ========================================================

    const cachedTerminals =
      await loadCache(
        POS_CACHE_KEYS.terminals
      );

    state.terminals =
      Array.isArray(
        cachedTerminals
      )
        ? cachedTerminals
        : [];

    if (
      getActiveTerminals()
        .length ===
      0
    ) {
      state.terminalError =
        "No cached active POS terminals are available.";
    }
    else {
      state.terminalError =
        null;
    }

    console.log(
      "[POS] Terminal fallback result:",
      state.terminals
    );

    console.log(
      "[POS] Connectivity after terminal fallback:",
      offlineManager
        .getStatus()
    );
  }
  finally {
    state.loadingTerminals =
      false;
  }
}

// ============================================================
// OPEN TERMINAL SELECTOR
// ============================================================

async function openTerminal() {
  state.terminalError =
    null;

  if (
    hasActiveSession()
  ) {
    state.terminalError =
      "End the current POS session before changing terminals.";

    return;
  }

  try {
    // Refresh on each opening. Do not rely on stale cache or Offline state.
    await loadTerminals();

    const activeTerminals =
      getActiveTerminals();

    if (
      activeTerminals.length ===
      0
    ) {
      state.terminalError =
        isOnline()
          ? "No active POS terminals are available."
          : "No cached active POS terminals are available.";
    }

    terminalSelectorDialog.value =
      true;

    console.log(
      "[POS] Opening terminal selector:",
      {
        online:
          isOnline(),

        terminalCount:
          state.terminals?.length
          ??
          0,

        activeTerminalCount:
          activeTerminals.length
      }
    );
  }
  catch (error) {
    console.error(
      "[POS] Unable to open terminal selector:",
      error
    );

    state.terminalError =
      getErrorMessage(
        error,
        "Unable to load POS terminals."
      );

    terminalSelectorDialog.value =
      true;
  }
}

// ============================================================
// SELECT / USE TERMINAL
// ============================================================

async function selectTerminal(
  terminal
) {
  state.terminalError =
    null;

  if (
    hasActiveSession()
  ) {
    state.terminalError =
      "End the current POS session before changing terminals.";

    return;
  }

  const terminalId =
    getTerminalId(
      terminal
    );

  if (!terminalId) {
    state.terminalError =
      "The selected POS terminal is invalid.";

    return;
  }

  if (
    !isTerminalActive(
      terminal
    )
  ) {
    state.terminalError =
      "The selected POS terminal is inactive.";

    return;
  }

  const previousTerminalId =
    getTerminalId(
      state.terminal
    );

  if (
    previousTerminalId &&
    String(
      previousTerminalId
    ) !==
    String(
      terminalId
    )
  ) {
    posStore.resetTransaction();
  }

  applyTerminal(
    terminal
  );

  terminalSelectorDialog.value =
    false;

  // ==========================================================
  // OFFLINE
  // ==========================================================

  if (
    !isOnline()
  ) {
    const localSession =
      shouldPersistSession.value
        ? await posSessionOfflinePersistence
            .getOpenByTerminalId(
              terminalId
            )
        : null;

    applySession(localSession ?? null);

    posPersistence.save(
      state
    );

    console.log(
      localSession
        ? "[POS] Existing offline session restored."
        : "[POS] Cached terminal selected offline."
    );

    return;
  }

  // ==========================================================
  // ONLINE
  // ==========================================================

  await loadTerminalSession();
}

// ============================================================
// CLOSE RUNTIME TERMINAL
// ============================================================

function closeTerminal() {
  state.terminalError =
    null;

  if (
    hasActiveSession()
  ) {
    state.terminalError =
      "End the current POS session before closing the terminal.";

    return;
  }

  const terminalId =
    getTerminalId(
      state.terminal
    );

  if (!terminalId) {
    return;
  }

  posStore.resetTerminal();

  posPersistence.save(
    state
  );

  console.log(
    "[POS] Runtime terminal closed:",
    terminalId
  );
}

// ============================================================
// LOAD TERMINAL SESSION
//
// ONLINE:
//   backend current session.
//
// OFFLINE:
//   IndexedDB posSessions.
// ============================================================

async function loadTerminalSession() {
  state.loadingSession =
    true;

  state.sessionError =
    null;

  try {
    const terminalId =
      getTerminalId(
        state.terminal
      );

    if (!terminalId) {
      applySession(null);

      return;
    }

    // ========================================================
    // OFFLINE
    // ========================================================

    if (
      !isOnline()
    ) {
      const localSession =
        await posSessionOfflinePersistence
          .getOpenByTerminalId(
            terminalId
          );

      applySession(isSessionOpen(localSession) ? localSession : null);

      if (isSessionOpen(localSession)) {
        console.log(
          "[POS] Offline terminal session restored:",
          localSession
        );
      }
      else {
        console.log(
          "[POS] Cached terminal selected with no local session."
        );
      }

      return;
    }

    // ========================================================
    // ONLINE
    // ========================================================

    const session =
      await posSessionService
        .getCurrentTerminalSession(
          terminalId
        );

    offlineManager
      .reportNetworkSuccess(
        "pos-current-session-load-success"
      );

    if (
      isSessionOpen(session)
    ) {
      applySession(session);

      await persistSessionForTerminalMode(
        session
      );

      console.log(
        "[POS] Existing terminal session restored:",
        session
      );

      return;
    }

    applySession(null);

    console.log(
      "[POS] Terminal selected with no active session."
    );
  }
  catch (error) {
    if (
      error?.response
    ) {
      /*
       * An HTTP response, including 404, proves the backend was
       * reachable.
       */
      offlineManager
        .reportNetworkSuccess(
          "pos-current-session-http-response"
        );
    }
    else {
      offlineManager
        .reportNetworkFailure(
          error,
          "pos-current-session-network-failure"
        );
    }

    const terminalId =
      getTerminalId(
        state.terminal
      );

    let localSession =
      null;

    if (
      terminalId
      &&
      shouldPersistSession.value
    ) {
      try {
        localSession =
          await posSessionOfflinePersistence
            .getOpenByTerminalId(
              terminalId
            );
      }
      catch (localError) {
        console.warn(
          "[POS] Local session lookup failed:",
          localError
        );
      }
    }

    if (localSession) {
      applySession(localSession);

      console.log(
        "[POS] Local session restored after backend lookup failure:",
        localSession
      );

      return;
    }

    applySession(null);

    if (
      error?.response?.status !==
      404
    ) {
      state.sessionError =
        getErrorMessage(
          error,
          "Unable to determine the current POS session."
        );
    }
  }
  finally {
    state.loadingSession =
      false;
  }
}

function isNetworkUnavailable(
  error
) {
  if (!error) {
    return false;
  }

  /*
   * Axios network failures generally have no HTTP response.
   */
  if (
    error.response ===
    undefined ||
    error.response ===
    null
  ) {
    const code =
      String(
        error.code
        ??
        ""
      )
        .trim()
        .toUpperCase();

    const message =
      String(
        error.message
        ??
        ""
      )
        .trim()
        .toUpperCase();

    return (
      code === "ERR_NETWORK" ||
      code === "ECONNABORTED" ||
      code === "ETIMEDOUT" ||
      message.includes(
        "NETWORK ERROR"
      ) ||
      message.includes(
        "FAILED TO FETCH"
      ) ||
      message.includes(
        "NETWORK"
      )
    );
  }

  return false;
}

// ============================================================
// CREATE OFFLINE SESSION
//
// Single implementation used when:
//
// 1. application already knows it is offline, OR
// 2. backend session opening fails because connectivity is gone.
// ============================================================

async function createOfflineSession(
  payload
) {
  const selectedTerminalId =
    getTerminalId(
      state.terminal
    );

  if (!selectedTerminalId) {
    throw new Error(
      "The POS terminal could not be resolved for offline session creation."
    );
  }

  // ==========================================================
  // CREATE LOCAL SESSION
  // ==========================================================

  const localSession =
    await posSessionOfflinePersistence
      .createOfflineSession({
        terminal:
          state.terminal,

        openingCashAmount:
          payload.openingCashAmount,

        currencyCode:
          payload.currencyCode,

        notes:
          payload.notes,

        userId:
          getCurrentUserId()
      });

  // Preserve the selected currency in runtime state even if an
  // older offline persistence implementation omits this field.
  applySession({
    ...localSession,
    currencyCode: payload.currencyCode
  });

  // ==========================================================
  // QUEUE BACKEND SESSION OPEN
  // ==========================================================

  const queueId =
    await syncQueue.enqueue({
      type:
        "POS_SESSION_OPEN",

      payload: {
        clientSessionId:
          localSession
            .clientSessionId,

        terminalId:
          selectedTerminalId,

        openingCashAmount:
          payload.openingCashAmount,

        currencyCode:
          payload.currencyCode,

        notes:
          payload.notes,

        openedAtUtc:
          localSession
            .openedAtUtc
      },

      metadata: {
        clientSessionId:
          localSession
            .clientSessionId,

        terminalId:
          selectedTerminalId
      }
    });

  // ==========================================================
  // MARK LOCAL SESSION QUEUED
  // ==========================================================

  const queuedSession =
    await posSessionOfflinePersistence
      .markQueued(
        localSession,
        queueId
      );

  applySession({
    ...(queuedSession ?? localSession),
    currencyCode: payload.currencyCode
  });

  startSessionDialog.value =
    false;

  posPersistence.save(
    state
  );

  console.log(
    "[POS] Offline session created:",
    {
      queueId,

      session:
        state.session
    }
  );

  return state.session;
}
// ============================================================
// OPEN START SESSION DIALOG
// ============================================================

async function openStartSession() {
  state.sessionError =
    null;

  if (
    hasActiveSession()
  ) {
    return;
  }

  if (
    !getTerminalId(
      state.terminal
    )
  ) {
    await openTerminal();

    return;
  }

  if (
    !isTerminalActive(
      state.terminal
    )
  ) {
    state.sessionError =
      "The selected POS terminal is inactive.";

    return;
  }

  startSessionDialog.value =
    true;
}

// ============================================================
// OPEN CLOSE SESSION DIALOG
// ============================================================

function openCloseSession() {
  state.sessionError =
    null;

  if (
    !hasActiveSession()
  ) {
    return;
  }

  closeSessionDialog.value =
    true;
}

// ============================================================
// START SESSION
//
// OFFLINE:
//   local posSessions record + POS_SESSION_OPEN queue.
//
// ONLINE:
//   normal backend session.
// ============================================================

async function startSession(
  request
) {
  if (
    state.sessionBusy
  ) {
    return;
  }

  if (
    hasActiveSession()
  ) {
    startSessionDialog.value =
      false;

    return;
  }

  const selectedTerminalId =
    getTerminalId(
      state.terminal
    );

  if (!selectedTerminalId) {
    state.sessionError =
      "Select a POS terminal before starting a session.";

    startSessionDialog.value =
      false;

    return;
  }

  const requestedTerminalId =
    request?.terminalId
    ??
    selectedTerminalId;

  if (
    String(
      requestedTerminalId
    ) !==
    String(
      selectedTerminalId
    )
  ) {
    state.sessionError =
      "The session terminal does not match the selected POS terminal.";

    return;
  }

  if (
    !isTerminalActive(
      state.terminal
    )
  ) {
    state.sessionError =
      "The selected POS terminal is inactive.";

    return;
  }

  const currencyCode = String(
    request?.currencyCode ?? ""
  ).trim().toUpperCase();

  if (
    !normalizeCurrencyCode(currencyCode) ||
    !sessionCurrencies.some(item => item.value === currencyCode)
  ) {
    state.sessionError =
      "Select a valid session currency before starting.";
    return;
  }

  const openingCashAmount = Number(request?.openingCashAmount);

  if (
    request?.openingCashAmount === null ||
    request?.openingCashAmount === undefined ||
    request?.openingCashAmount === "" ||
    !Number.isFinite(openingCashAmount) ||
    openingCashAmount < 0
  ) {
    state.sessionError =
      "Opening cash amount must be a non-negative number.";
    return;
  }

  state.sessionBusy =
    true;

  state.sessionError =
    null;

  const payload = {
    terminalId:
      selectedTerminalId,

    openingCashAmount,

    currencyCode,

    notes:
      request?.notes
      ??
      null
  };

  try {
    // ========================================================
    // KNOWN OFFLINE
    // ========================================================

    if (
      !isOnline()
    ) {
      await createOfflineSession(
        payload
      );

      return;
    }

    // ========================================================
    // TRY ONLINE BACKEND
    // ========================================================

    try {
      // ======================================================
      // ASSISTED / MOBILE SESSION CONTINUITY
      //
      // Before creating a new session, first check whether the
      // selected terminal already has an active backend session.
      //
      // 200 + session:
      //   restore it and continue using the same cashier session.
      //
      // 404:
      //   no active backend session exists, so create a new one.
      //
      // network failure:
      //   switch to the existing offline session flow.
      // ======================================================

      let existingSession =
        null;

      try {
        existingSession =
          await posSessionService
            .getCurrentTerminalSession(
              selectedTerminalId
            );

        offlineManager
          .reportNetworkSuccess(
            "pos-existing-session-load-success"
          );
      }
      catch (lookupError) {
        const status =
          lookupError?.response?.status
          ??
          lookupError?.status
          ??
          lookupError?.data?.status
          ??
          null;

        if (
          status ===
          404
        ) {
          /*
           * 404 means the backend is reachable but there is no
           * active session for this terminal.
           */
          offlineManager
            .reportNetworkSuccess(
              "pos-existing-session-not-found"
            );

          existingSession =
            null;
        }
        else {
          const networkFailure =
            offlineManager
              .reportNetworkFailure(
                lookupError,
                "pos-existing-session-network-failure"
              );

          if (
            networkFailure
          ) {
            console.warn(
              "[POS] Backend unavailable while checking the current session. " +
              "Switching to offline POS session.",
              lookupError
            );

            await createOfflineSession(
              payload
            );

            return;
          }

          if (
            lookupError?.response
          ) {
            offlineManager
              .reportNetworkSuccess(
                "pos-existing-session-http-response"
              );
          }

          throw lookupError;
        }
      }

      // ======================================================
      // RESTORE EXISTING BACKEND SESSION
      // ======================================================

      if (
        existingSession &&
        getSessionId(
          existingSession
        )
      ) {
        applySession(existingSession);

        await persistSessionForTerminalMode(
          existingSession
        );

        state.terminal = {
          ...(state.terminal || {}),

          id:
            existingSession.terminalId
            ??
            state.terminal?.id
            ??
            selectedTerminalId,

          terminalId:
            existingSession.terminalId
            ??
            state.terminal?.terminalId
            ??
            selectedTerminalId,

          terminalCode:
            existingSession.terminalCode
            ??
            state.terminal?.terminalCode
            ??
            "",

          terminalName:
            existingSession.terminalName
            ??
            state.terminal?.terminalName
            ??
            ""
        };

        startSessionDialog.value =
          false;

        posPersistence.save(
          state
        );

        console.log(
          "[POS] Existing active POS session restored:",
          existingSession
        );

        return;
      }

      // ======================================================
      // NO EXISTING SESSION — CREATE A NEW BACKEND SESSION
      // ======================================================

      console.log(
        "[POS] Starting backend POS session:",
        payload
      );

      const session =
        await posSessionService
          .openSession(
            payload
          );

      if (
        !session ||
        !getSessionId(
          session
        )
      ) {
        throw new Error(
          "The backend did not return a valid POS session."
        );
      }

      offlineManager
        .reportNetworkSuccess(
          "pos-session-open-success"
        );

      applySession(session);

      state.terminal = {
        ...(state.terminal || {}),

        id:
          session.terminalId
          ??
          state.terminal?.id
          ??
          selectedTerminalId,

        terminalId:
          session.terminalId
          ??
          state.terminal?.terminalId
          ??
          selectedTerminalId,

        terminalCode:
          session.terminalCode
          ??
          state.terminal?.terminalCode
          ??
          "",

        terminalName:
          session.terminalName
          ??
          state.terminal?.terminalName
          ??
          ""
      };

      await persistSessionForTerminalMode(
        session
      );

      startSessionDialog.value =
        false;

      posPersistence.save(
        state
      );

      console.log(
        "[POS] Online session started:",
        session
      );

      return;
    }
    catch (error) {
      // ======================================================
      // NETWORK DISAPPEARED
      //
      // Browser may still claim it is online.
      // Fall back to local session instead of showing failure.
      // ======================================================

      const networkFailure =
        offlineManager
          .reportNetworkFailure(
            error,
            "pos-session-open-network-failure"
          );

      if (
        networkFailure
      ) {
        console.warn(
          "[POS] Backend unavailable. Switching to offline POS session.",
          error
        );

        await createOfflineSession(
          payload
        );

        return;
      }

      /*
       * A real HTTP response proves connectivity exists even
       * when the backend rejected the request.
       */
      if (
        error?.response
      ) {
        offlineManager
          .reportNetworkSuccess(
            "pos-session-open-http-response"
          );
      }

      /*
       * Real backend validation errors must NOT be converted
       * into offline sessions.
       *
       * Examples:
       * 400 invalid request
       * 401 unauthorized
       * 403 forbidden
       * 409 session conflict
       */
      throw error;
    }
  }
  catch (error) {
    console.error(
      "[POS] Start session failed:",
      error
    );

    state.sessionError =
      getErrorMessage(
        error,
        "Unable to start the POS session."
      );
  }
  finally {
    state.sessionBusy =
      false;
  }
}
// ============================================================
// CLOSE SESSION
// ============================================================

async function closeSession(
  request
) {
  if (
    state.sessionBusy
  ) {
    return;
  }

  const sessionId =
    request?.sessionId
    ??
    getSessionId(
      state.session
    );

  if (!sessionId) {
    state.sessionError =
      "No active POS session was found.";

    return;
  }

  state.sessionBusy =
    true;

  state.sessionError =
    null;

  const payload = {
    closingCashAmount:
      Number(
        request?.closingCashAmount
        ??
        0
      ),

    notes:
      request?.notes
      ??
      null
  };

  try {
    // ========================================================
    // OFFLINE CLOSE
    // ========================================================

    if (
      !isOnline()
    ) {
      const clientSessionId =
        state.session?.clientSessionId
        ??
        (
          state.session?.wasCreatedOffline ===
          true
            ? sessionId
            : null
        );

      let localSession =
        null;

      if (clientSessionId) {
        localSession =
          await posSessionOfflinePersistence
            .closeLocalSession(
              clientSessionId,
              payload
            );
      }

      const queueId =
        await syncQueue.enqueue({
          type:
            "POS_SESSION_CLOSE",

          payload: {
            clientSessionId:
              clientSessionId
              ??
              null,

            serverSessionId:
              state.session?.serverSessionId
              ??
              (
                state.session?.wasCreatedOffline ===
                true
                  ? null
                  : sessionId
              ),

            terminalId:
              getTerminalId(
                state.terminal
              ),

            closingCashAmount:
              payload.closingCashAmount,

            notes:
              payload.notes,

            closedAtUtc:
              new Date()
                .toISOString()
          },

          metadata: {
            clientSessionId:
              clientSessionId
              ??
              null,

            terminalId:
              getTerminalId(
                state.terminal
              )
          }
        });

      posStore.resetSession();
      state.currency = null;

      closeSessionDialog.value =
        false;

      posPersistence.save(
        state
      );

      console.log(
        "[POS] Offline session close queued:",
        {
          queueId,
          localSession
        }
      );

      return;
    }

    // ========================================================
    // ONLINE CLOSE
    // ========================================================

    const closingSession =
      state.session;

    const backendSessionId =
      closingSession?.serverSessionId
      ??
      sessionId;

    await posSessionService
      .closeSession(
        backendSessionId,
        payload
      );

    offlineManager
      .reportNetworkSuccess(
        "pos-session-close-success"
      );

    await deleteLocalSessionAfterOnlineClose(
      closingSession
    );

    posStore.resetSession();
    state.currency = null;

    closeSessionDialog.value =
      false;

    posPersistence.save(
      state
    );

    console.log(
      "[POS] Session closed. Terminal remains selected:",
      state.terminal
    );
  }
  catch (error) {
    if (
      error?.response
    ) {
      offlineManager
        .reportNetworkSuccess(
          "pos-session-close-http-response"
        );
    }
    else {
      offlineManager
        .reportNetworkFailure(
          error,
          "pos-session-close-network-failure"
        );
    }

    console.error(
      "[POS] Close session failed:",
      error
    );

    state.sessionError =
      getErrorMessage(
        error,
        "Unable to close the POS session."
      );
  }
  finally {
    state.sessionBusy =
      false;
  }
}

// ============================================================
// ERROR MESSAGE
// ============================================================

function getErrorMessage(
  error,
  fallback
) {
  const data =
    error?.response?.data;

  if (
    typeof data ===
    "string"
  ) {
    return data;
  }

  if (
    data?.errors &&
    typeof data.errors ===
      "object"
  ) {
    const validationMessages =
      Object.values(
        data.errors
      )
        .flat()
        .filter(
          Boolean
        );

    if (
      validationMessages.length
    ) {
      return validationMessages
        .join(" ");
    }
  }

  return (
    data?.message
    ??
    data?.detail
    ??
    data?.title
    ??
    error?.message
    ??
    fallback
  );
}

// ============================================================
// RESPONSIVE SIDEBAR
// ============================================================

watch(
  mdAndUp,

  desktop => {
    if (
      desktop
    ) {
      sidebarOpen.value =
        true;

      return;
    }

    sidebarOpen.value =
      false;
  },

  {
    immediate: true
  }
);

// ============================================================
// TERMINAL / SESSION PERSISTENCE
// ============================================================

watch(
  () => [
    state.terminal,
    state.session
  ],

  () => {
    if (
      !persistenceReady.value
    ) {
      return;
    }

    posPersistence.save(
      state
    );
  },

  {
    deep: true
  }
);

// ============================================================
// INITIALIZE POS
// ============================================================

onMounted(
  async () => {
    /*
     * Capture runtime persistence before terminal/session
     * initialization.
     */
    try {
      await posSyncProcessor.init();
    } catch (syncInitError) {
      console.error("[POS] Sync processor initialization failed:", syncInitError);
    }

    const persisted =
      posPersistence.load();

      
    // ========================================================
    // LOAD TERMINAL SOURCE
    //
    // Online  -> backend + IndexedDB cache
    // Offline -> IndexedDB
    // ========================================================

    await loadTerminals();

    // ========================================================
    // RESTORE PREVIOUS TERMINAL
    // ========================================================

    if (
      persisted?.terminalId
    ) {
      const terminal =
        state.terminals.find(
          item =>
            String(
              getTerminalId(
                item
              )
            ) ===
            String(
              persisted.terminalId
            )
        );

      if (
        terminal &&
        isTerminalActive(
          terminal
        )
      ) {
        applyTerminal(
          terminal
        );

        /*
         * Online:
         * backend current session.
         *
         * Offline:
         * IndexedDB local session.
         */
        await loadTerminalSession();
      }
    }

    /*
     * Startup restoration is now complete.
     */
    persistenceReady.value =
      true;

    console.log(
      "[POS] Layout initialized:",
      {
        online:
          isOnline(),

        terminals:
          state.terminals?.length
          ??
          0,

        terminal:
          state.terminal,

        session:
          state.session
      }
    );
  }
);
</script>

<style scoped>
/* ============================================================
   APPLICATION
============================================================ */

.pos-app {
  min-height: 100vh;
}

.pos-app
:deep(.v-application__wrap) {
  min-height: 100vh;

  display: flex;
  flex-direction: column;
}

/* ============================================================
   SHELL
============================================================ */

.pos-shell {
  width: 100%;

  flex:
    1
    1
    auto;

  display: flex;

  position: relative;

  min-width: 0;
  min-height: 0;

  background: #f7f8fa;
}

/* ============================================================
   DESKTOP SIDEBAR
============================================================ */

.pos-sidebar-desktop {
  width: 56px;
  min-width: 56px;

  flex:
    0
    0
    56px;

  align-self: stretch;

  position: relative;

  z-index: 50;

  background: #202328;

  overflow: visible;

  border-right:
    1px solid
    rgba(
      0,
      0,
      0,
      0.08
    );
}

.pos-sidebar-desktop
:deep(.pos-sidebar) {
  width: 56px;
  min-width: 56px;

  min-height: 100%;
}

/* ============================================================
   MOBILE DRAWER
============================================================ */

.pos-navigation-drawer {
  z-index: 2000;
}

.pos-navigation-drawer
:deep(.v-navigation-drawer__content) {
  background: #303030;
}

.pos-navigation-drawer
:deep(.pos-sidebar) {
  min-height: 100%;
}

/* ============================================================
   CONTENT
============================================================ */

.pos-main {
  flex:
    1
    1
    auto;

  width: 100%;

  min-width: 0;
  min-height: 0;

  overflow-x: hidden;

  background: #f7f8fa;
}

/* ============================================================
   FOOTER
============================================================ */

:deep(.pos-support-footer),
:deep(.hosting-support-footer) {
  flex: 0 0 auto;
  flex-shrink: 0;
}

/* ============================================================
   RESPONSIVE gfg
============================================================ */

@media (
  max-width: 959px
) {
  .pos-main {
    width: 100%;
  }
}
</style>